import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Formik, Form } from 'formik';

import { PlanEnum, isAllowedByolPlan } from 'api-bible-shared-lib/utils/types';

import { Alert, AlertType } from '../components/Alert';
import Container from '../components/Container';
import Card from '../components/Card';
import ConfirmDialog from '../components/ConfirmDialog';
import PlanSelection from '../components/PlanSelection';
import { Button } from '../components/Button';
import { AppHeader } from '../components/AppHeader';

import { useApplications } from '../contexts/Applications';
import { usePlans } from '../contexts/Plans';

export default function ApplicationPlan({ appId }: { appId: string }) {
  const history = useHistory();
  const { applicationsById, changeApplicationPlan, cancelApplicationPlan } = useApplications();

  const { plansById } = usePlans();

  const app = applicationsById[appId];

  const [error, setError] = useState<Error | undefined>(undefined);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!app) return null; // can't arrive here since router will redirect

  const onSubmit = async (values: any, { setSubmitting }: any) => {
    setError(undefined);

    try {
      if (values.formSubmitted) {
        setSubmitting(true);
        setShowConfirm(false);

        if (values.cancelPlan) {
          await cancelApplicationPlan(app.id, app.status);
        } else if (values.selectedPlan) {
          await changeApplicationPlan(app.id, app.plan, values.selectedPlan);
        }
        history.push(`/application/${app.id}/dashboard`);
      }
    } catch (e) {
      const err = e as Error;
      setError(err);
    }

    setSubmitting(false);
  };

  return (
    <Container>
      <AppHeader appLinkBasePath="/application" currentAppId={appId} currentAppMenuItem="plan" />
      <div className="flex justify-between mb-4">
        <Card className="px-12 py-12">
          {error && (
            <Alert
              className="mb-8"
              type={AlertType.ERROR}
              title="There was an error with your submission"
              message={error?.message || 'An unexpected error occurred.'}
            />
          )}

          <Formik
            initialValues={{ selectedPlan: '', formSubmitted: false, cancelPlan: false }}
            onSubmit={onSubmit}
          >
            {({ values, isSubmitting, submitForm, setFieldValue }) => (
              <Form>
                <div className="flex flex-col items-center w-full lg:items-stretch lg:flex-row">
                  <PlanSelection
                    currentPlan={app.plan}
                    isAllowedByolPlan={isAllowedByolPlan(app.dblToken, app.authorizedBibles)}
                    selectedPlan={values.selectedPlan as PlanEnum}
                    handlePlanSelect={(plan) => {
                      setFieldValue('formSubmitted', true);
                      setFieldValue('selectedPlan', plan);
                      setFieldValue('cancelPlan', false);
                      setShowConfirm(true);
                    }}
                  />
                </div>
                <div className="flex flex-row-reverse justify-between mt-6 ">
                  <Button
                    outline
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    onClick={() => {
                      setFieldValue('formSubmitted', true);
                      setFieldValue('selectedPlan', undefined);
                      setFieldValue('cancelPlan', true);
                      setShowConfirm(true);
                    }}
                    type="button"
                  >
                    Cancel Plan Subscription
                  </Button>
                </div>
                {showConfirm && values.selectedPlan && (
                  <ConfirmDialog
                    body={
                      <div>
                        Are you sure you want to switch to switch from {plansById[app.plan].name} to{' '}
                        <span key="confirm-pause" className="text-red-600">
                          {plansById[values.selectedPlan].name}
                        </span>
                        {'? '}
                        {app.plan === PlanEnum.FREE
                          ? 'You will be directed to Stripe for payment.'
                          : 'Any prorated amount from your current plan will be credited to your next invoice.'}
                      </div>
                    }
                    handleCancel={() => setShowConfirm(false)}
                    handleConfirm={() => submitForm()}
                  />
                )}
                {showConfirm && values.cancelPlan && (
                  <ConfirmDialog
                    body={
                      <div>
                        Are you sure you want to <span className="text-red-600">cancel</span> this
                        application? You will no longer be able to use this API key.
                      </div>
                    }
                    handleCancel={() => setShowConfirm(false)}
                    handleConfirm={() => submitForm()}
                  />
                )}
              </Form>
            )}
          </Formik>
        </Card>
      </div>
    </Container>
  );
}
